import { Dispatch, FocusEvent, RefObject, SetStateAction } from 'react';
import { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';
import { KeyboardLayouts } from '../../controls/SimpleKeyboard/Layout';

export type GetValueReturn = {
    inputValue: string;
    caretPositionStart: number;
    caretPositionEnd: number;
};
export type SetValueProps = {
    value: string;
    caretPosition: number;
};
export type OnInputChangeProps = {
    value: string;
    caretPosition: number;
    wasBackspace: boolean;
    wasSelectionRange: boolean;
};
export type OnInputChangeReturn = {
    value: string;
    caretPosition: number;
};

export interface GetShiftLayoutProps {
    layout: KeyboardLayouts;
    keyboardValue: string;
    capsLock: boolean;
}

export enum KeyboardDispatchTypes {
    /**
     * Passes in all of the necessary properties from the input field via
     * the useKeyboardIntegration hook when the input field is focused.
     */
    OnFieldFocused = 'On_Field_Focused',
    /**
     * Updates the functions with each field re-render.
     */
    OnFieldRerender = 'On_Field_Rerender',
    /**
     * When the input field is blurred, the useKeyboardIntegration hook calls
     * this dispatch to set the fieldFocused boolean to false. Some components
     * use this for their internal logic.
     */
    OnFieldBlurred = 'On_Field_Blurred',
    /**
     * When the onKeyboardInput function is called to refocus the current input element,
     * this dispatch is used to update the inputFocused property to true since
     * onFieldBlurred would have made it false.
     */
    OnKeyboardInput = 'On_Keyboard_Input',
    /**
     * Dispatches the new keyboard layout to the context.
     */
    KeyboardLayoutChanged = 'Keyboard_Layout_Changed',
    /**
     * Dispatches to the context to open the keyboard.
     */
    KeyboardOpened = 'Keyboard_Opened',
    /**
     * Dispatches to the context to close the keyboard.
     */
    KeyboardClosed = 'Keyboard_Closed',
    /**
     * Dispatches to the context on whether to hide or show the keyboard.
     */
    KeyboardHidden = 'KeyboardHidden',
    /**
     * Used by the keyboard to dispatch a layout change to the context when
     * caps lock is enabled to use the shifted layout.
     */
    CapsLockChanged = 'CapsLock_Changed',
    /**
     * Used by the keyboard when a click event happens outside of the field or keyboard so that
     * if the hideKeyboardOnClickout is true, the keyboard hides.
     */
    ClickOutOfKeyboard = 'Click_Out_Of_Keyboard',
}

export type KeyboardPayload = {
    [KeyboardDispatchTypes.OnFieldFocused]: {
        inputElement: HTMLInputElement;
        keyboardLayout: KeyboardLayouts;
        shiftAfterPeriod: boolean;
        shiftAfterSpace: boolean;
        shiftAfterAmpersand: boolean;
        hideKeyboardOnClickOut: boolean;
        capsLock: boolean;
        getValue: () => GetValueReturn;
        setValue: (props: SetValueProps) => void;
        onInputChange: (props: OnInputChangeProps) => OnInputChangeReturn;
        onKeyboardInput: VoidFunction;
    };
    [KeyboardDispatchTypes.OnFieldRerender]: {
        getValue: () => GetValueReturn;
        setValue: (props: SetValueProps) => void;
        onInputChange: (props: OnInputChangeProps) => OnInputChangeReturn;
        onKeyboardInput: VoidFunction;
    };
    [KeyboardDispatchTypes.OnFieldBlurred]: undefined;
    [KeyboardDispatchTypes.OnKeyboardInput]: undefined;
    [KeyboardDispatchTypes.KeyboardLayoutChanged]: KeyboardLayouts;
    [KeyboardDispatchTypes.KeyboardOpened]: undefined;
    [KeyboardDispatchTypes.KeyboardClosed]: undefined;
    [KeyboardDispatchTypes.KeyboardHidden]: boolean;
    [KeyboardDispatchTypes.CapsLockChanged]: { capslock: boolean; keyboardLayout: KeyboardLayouts; };
    [KeyboardDispatchTypes.ClickOutOfKeyboard]: undefined;
};

export type KeyboardActions = ActionMap<KeyboardPayload>[keyof ActionMap<KeyboardPayload>];

export type KeyboardProviderProps = { children: React.ReactNode; };

export type KeyboardState = {
    /**
     * Boolean for whether the current input element is focused or not.
     */
    inputFocused: boolean;
    /**
     * Stores a reference to the input element currently focused.
     */
    inputElement: HTMLInputElement | undefined;
    /**
     * Stores the current keyboard layout for the on-screen keyboard.
     */
    keyboardLayout: KeyboardLayouts;
    /**
     * Boolean for whether the keyboard is open.
     */
    keyboardOpen: boolean;
    /**
     * Boolean for whether the keyboard should be hidden.
     */
    keyboardHidden: boolean;
    /**
     * Boolean for whether the layout should use the shift layout version
     * if available.
     */
    capsLock: boolean;
    /**
     * Boolean for whether we should shift the keyboard layout after a period is entered.
     */
    shiftAfterPeriod: boolean;
    /**
     * Boolean for whether we should shift the keyboard layout after a space is entered.
     */
    shiftAfterSpace: boolean;
    /**
     * Boolean for whether we should shift the keyboard layout after an ampersand is entered.
     */
    shiftAfterAmpersand: boolean;
    /**
     * Boolean for whether we should hide the keyboard when we click outside of the
     * currently focused input element and on-screen keyboard.
     */
    hideKeyboardOnClickOut: boolean;
    /**
     * Function for the input element to pass the current internal value,
     * caret start position, and caret end position.
     */
    getValue: () => GetValueReturn;
    /**
     * Function for the on-screen keyboard to return to the input element the new caret position,
     * and input value after the on-screen keyboard onChange triggers.
     */
    setValue: (props: SetValueProps) => void;
    /**
     * Function for the input element to change the on-screen keyboard onChange value and caret position
     * based on specific requirements of the input element.
     */
    onInputChange: (props: OnInputChangeProps) => OnInputChangeReturn;
    /**
     * Allows the on-screen keyboard to forcibly focus the input element if it is not currently focused.
     */
    onKeyboardInput: VoidFunction;
};

/**
 * External Facing Props
 * ---------------------
 * These props are exposed and can be placed on GM inputs
 */
export interface ExternalUseKeyboardIntegrationProps {
    /**
     * Passes in the keyboard layout to dispatch to the on-screen keyboard.
     */
    keyboardLayout?: KeyboardLayouts;
    /**
     * Auto-shifts after a period is entered.
     */
    shiftAfterPeriod?: boolean;
    /**
     * Auto-shifts after a space is entered.
     */
    shiftAfterSpace?: boolean;
    /**
     * Auto-shifts after an ampersand is entered.
     */
    shiftAfterAmpersand?: boolean;
    /**
     * Hides the keyboard when you click somewhere outside of the field and keyboard.
     */
    hideKeyboardOnClickOut?: boolean;
    /**
     * Changes the keyboard layout to be a shift layout if applicable
     */
    capsLock?: boolean;
    /**
     * Clears input when you blur the field
     */
    clearOnBlur?: boolean;
}

/**
 * Internal Facing Props
 * ---------------------
 * These props are only available in the component wrappers for Kiosk components
 */
export interface InternalUseKeyboardIntegrationProps {
    /**
     * Passes in the field value for the keyboard
     */
    fieldValue: string;
    /**
     * Passes the inputs caret position start and end as well as the current input value to the
     * on screen keyboard by refs.
     */
    getValue: GoMotoFunction<[void], GetValueReturn>;
    /**
     * Passes the keyboard onChange value and new caret position to the input to update the internal
     * input state.
     */
    setValue: GoMotoFunction<[SetValueProps], void>;
    /**
     * This function allows the input to augment or sanitize the keyboard onChange value and caret position
     * for specific input requirements.
     */
    onInputChange: GoMotoFunction<[OnInputChangeProps], OnInputChangeReturn>;
    /**
     * This function allows the keyboard to refocus the current input element if it is unfocused and the keyboard still
     * has an input element it is targetting.
     */
    onKeyboardInput: VoidFunction;
}

export type UseKeyboardIntegrationProps = InternalUseKeyboardIntegrationProps & ExternalUseKeyboardIntegrationProps;
export type UseKeyboardIntegrationReturn = {
    onBlur: VoidFunction;
    onFocus: GoMotoFunction<[FocusEvent<Element>], void>;
};

/**
 * This object is used to extend TextField interfaces with kiosk props needed for when the
 * TextField exists inside of an electron instance.
 */
export interface ExternalUseKeyboardFormFieldProps {
    /**
     * Passes in the keyboard layout to dispatch to the on-screen keyboard.
     */
    keyboardLayout?: KeyboardLayouts;
    /**
     * Auto-shifts after a period is entered.
     */
    shiftAfterPeriod?: boolean;
    /**
     * Auto-shifts after a space is entered.
     */
    shiftAfterSpace?: boolean;
    /**
     * Auto-shifts after an ampersand is entered.
     */
    shiftAfterAmpersand?: boolean;
    /**
     * Hides the keyboard when you click somewhere outside of the field and keyboard.
     */
    hideKeyboardOnClickOut?: boolean;
    /**
     * Changes the keyboard layout to be a shift layout if applicable
     */
    capsLock?: boolean;
    /**
     * Clears input when you blur the field
     */
    clearOnBlur?: boolean;
    /**
     * onChange callback to the component that uses the field to handle component specific
     * onChange logic.
     */
    onChangeCallback?: VoidFunction;
    /**
     * onBlur callback to the component that uses the field to handle component specific
     * onBlur logic.
     */
    onBlurCallback?: VoidFunction;
}

/**
 * This object is only used internally in TextField components so that declared functions and values created and
 * retrieved inside of the TextField can be passed as props to this hook.
 */
export interface InternalUseKeyboardFormFieldProps {
    /**
     * Formik field name for updating the values in the form.
     */
    name: string;
    /**
     * The value from the input on the field.
     */
    value: string;
    /**
     * Formik setter function for updating the form value for the field.
     */
    setFieldValue: GoMotoFunction<[string, string], void>;
    /**
     * Optional function to convert the caret position from the on-screen keyboard for the control.
     */
    setNewCaretPosition?: GoMotoFunction<[number, string?], number>;
    /**
     * Optional function to convert the caret position from the control for the on-screen keyboard.
     */
    getNewCaretPosition?: GoMotoFunction<[number, string?], number>;
    /**
     * Optional onInputChange function to handle specific on-screen keyboard onChange logic.
     */
    onInputChangeCallback?: GoMotoFunction<[OnInputChangeProps], OnInputChangeReturn>;
}

/**
 * The type definition of the useKeyboardFormField props.
 *
 * @extends InternalUseKeyboardFormFieldProps - {@link InternalUseKeyboardFormFieldProps} description.
 * @extends ExternalUseKeyboardFormFieldProps - {@link ExternalUseKeyboardFormFieldProps} description.
 */
export type KeyboardFormFieldProps = ExternalUseKeyboardFormFieldProps & InternalUseKeyboardFormFieldProps;

/**
 * The return object provided by the useKeyboardFormField hook. These objects are used
 * to maintain sync between the keyboard state and the TextField state.
 */
export type KeyboardFormFieldReturn = {
    /**
     * Ref to the input element
     */
    inputRef: RefObject<HTMLInputElement>;
    /**
     * Stored value for the on-screen keyboard to use to stay in sync with the field.
     */
    keyboardValue: string;
    /**
     * Setter function for the on-screen keyboard input value.
     */
    setKeyboardValue: Dispatch<SetStateAction<string>>;
    /**
     * Stored caret position for the on-screen keyboard to use to stay in sync with the field.
     */
    caretPosition: number | null;
    /**
     * Setter function for the on-screen keyboard caret position value.
     */
    setCaretPosition: Dispatch<SetStateAction<number | null>>;
    /**
     * Stored boolean for if the field is focused or not.
     */
    isFocused: boolean;
    /**
     * Function to handle on-screen keyboard specific blur logic.
     */
    onBlur: VoidFunction;
    /**
     * Function to handle on-screen keyboard specific focus logic.
     */
    onFocus: GoMotoFunction<[FocusEvent<Element>], void>;
    /**
     * Function to handle on-screen keyboard specific on change logic.
     */
    onChange: GoMotoFunction<[SetValueProps], void>;
};

/**
 * The function type definition for the useKeyboardFormField hook.
 */
export type KeyboardFormFieldFunction = GoMotoFunction<[KeyboardFormFieldProps], KeyboardFormFieldReturn>;